<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  deal: { type: Object, default: null },
  stages: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
  initialContactId: { type: [Number, String], default: null },
  initialContact: { type: Object, default: null },
  initialInboxId: { type: Number, default: null },
});

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const router = useRouter();

const isEditing = computed(() => props.deal && props.deal.id);
const isSubmitDisabled = computed(() => !form.value.contact_id);
const showInboxSelector = computed(() => props.inboxes.length > 0);
const filteredStages = computed(() => {
  if (!showInboxSelector.value) return props.stages;
  if (!form.value.inbox_id) return [];

  return props.stages.filter(
    stage => Number(stage.inbox_id) === Number(form.value.inbox_id)
  );
});

// Products from store
const allProducts = computed(() => store.getters['crmProducts/getActiveProducts']);

const form = ref({
  title: '',
  description: '',
  value: 0,
  priority: 'low',
  status: 'active',
  contact_id: '',
  inbox_id: '',
  crm_stage_id: '',
  assignee_id: '',
});

const isSubmitting = ref(false);
const submitError = ref('');

// Contact selector
const contactQuery = ref('');
const allContacts = ref([]);
const selectedContact = ref(null);
const showContactDropdown = ref(false);
const isLoadingContacts = ref(false);
const contactDropdownContainerRef = ref(null);

// Deal products
const dealProducts = ref([]);
const productQueryByRow = ref([]);
const showProductDropdownByRow = ref([]);
const productComboboxRefs = ref([]);
const existingAttachments = ref([]);
const newAttachments = ref([]);

const contactResults = computed(() => {
  const query = contactQuery.value.trim().toLowerCase();
  const sortedContacts = [...allContacts.value].sort((a, b) => {
    const aName = (a.name || a.email || '').toLowerCase();
    const bName = (b.name || b.email || '').toLowerCase();
    return aName.localeCompare(bName, 'pt-BR');
  });

  if (!query) {
    return sortedContacts;
  }

  return sortedContacts.filter(contact => {
    const name = (contact.name || '').toLowerCase();
    const email = (contact.email || '').toLowerCase();
    const phone = (contact.phone_number || '').toLowerCase();
    return name.includes(query) || email.includes(query) || phone.includes(query);
  });
});

const filteredProductsByRow = computed(() => {
  return dealProducts.value.map((_, index) => {
    const query = (productQueryByRow.value[index] || '').trim().toLowerCase();
    if (!query) {
      return allProducts.value;
    }

    return allProducts.value.filter(product =>
      (product.name || '').toLowerCase().includes(query)
    );
  });
});

function addProductRow() {
  dealProducts.value.push({ crm_product_id: '', quantity: 1, unit_price: 0 });
  productQueryByRow.value.push('');
  showProductDropdownByRow.value.push(true);
}

function removeProductRow(index) {
  dealProducts.value.splice(index, 1);
  productQueryByRow.value.splice(index, 1);
  showProductDropdownByRow.value.splice(index, 1);
  productComboboxRefs.value.splice(index, 1);
  recalcTotalFromProducts();
}

function setProductComboboxRef(el, index) {
  productComboboxRefs.value[index] = el;
}

function onProductInputFocus(index) {
  showProductDropdownByRow.value[index] = true;
}

function onProductInput(index) {
  showProductDropdownByRow.value[index] = true;

  const query = (productQueryByRow.value[index] || '').trim().toLowerCase();
  if (!query) {
    dealProducts.value[index].crm_product_id = '';
    dealProducts.value[index].unit_price = 0;
    recalcTotalFromProducts();
    return;
  }

  const selectedProduct = allProducts.value.find(
    product => product.id === Number(dealProducts.value[index].crm_product_id)
  );
  if (!selectedProduct) return;

  if (!selectedProduct.name?.toLowerCase().includes(query)) {
    dealProducts.value[index].crm_product_id = '';
    dealProducts.value[index].unit_price = 0;
    recalcTotalFromProducts();
  }
}

function selectProduct(index, product) {
  const dp = dealProducts.value[index];
  dp.crm_product_id = product.id;
  dp.unit_price = parseFloat(product.unit_price) || 0;
  productQueryByRow.value[index] = product.name || '';
  showProductDropdownByRow.value[index] = false;
  recalcTotalFromProducts();
}

function openSelectedProductEditor(index) {
  const productId = Number(dealProducts.value[index]?.crm_product_id);
  if (!productId) return;

  const accountId = router.currentRoute.value.params.accountId;
  emit('close');
  router.push({
    name: 'crm_products',
    params: { accountId },
    query: { edit_product_id: String(productId) },
  });
}

function recalcTotalFromProducts() {
  if (dealProducts.value.length === 0) return;
  const total = dealProducts.value.reduce((sum, dp) => {
    return sum + (dp.quantity || 0) * (dp.unit_price || 0);
  }, 0);
  form.value.value = parseFloat(total.toFixed(2));
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
}

onMounted(() => {
  document.addEventListener('mousedown', handleDocumentClick);
  document.addEventListener('keydown', handleEscapeKey);

  store.dispatch('crmProducts/fetch');

  const fallbackInboxId = props.initialInboxId || props.inboxes[0]?.id || '';

  if (props.deal) {
    if (props.deal.id) {
      const currentStage = props.stages.find(
        stage => stage.id === (props.deal.crm_stage_id || props.deal.crm_stage?.id)
      );

      form.value = {
        title: props.deal.title || '',
        description: props.deal.description || '',
        value: props.deal.value || 0,
        priority: props.deal.priority || 'low',
        status: props.deal.status || 'active',
        contact_id: props.deal.contact_id || props.deal.contact?.id || '',
        inbox_id: currentStage?.inbox_id || props.deal.crm_stage?.inbox_id || fallbackInboxId,
        crm_stage_id: props.deal.crm_stage_id || props.deal.crm_stage?.id || '',
        assignee_id: props.deal.assignee_id || props.deal.assignee?.id || '',
      };
      if (props.deal.contact) {
        selectedContact.value = props.deal.contact;
        contactQuery.value = props.deal.contact.name || '';
      }
      existingAttachments.value = props.deal.attachments || [];
      // Load existing deal products
      if (props.deal.crm_deal_products?.length) {
        dealProducts.value = props.deal.crm_deal_products.map(dp => ({
          crm_product_id: dp.crm_product_id || dp.crm_product?.id || '',
          quantity: dp.quantity || 1,
          unit_price: parseFloat(dp.unit_price) || 0,
        }));
        productQueryByRow.value = props.deal.crm_deal_products.map(dp => dp.crm_product?.name || '');
        showProductDropdownByRow.value = props.deal.crm_deal_products.map(() => false);
      }
    } else if (props.deal.crm_stage_id) {
      const currentStage = props.stages.find(
        stage => stage.id === props.deal.crm_stage_id
      );
      form.value.inbox_id = currentStage?.inbox_id || fallbackInboxId;
      form.value.crm_stage_id = props.deal.crm_stage_id;
    }
  }

  // If opened from contact panel with pre-set contact
  if (props.initialContactId) {
    form.value.contact_id = props.initialContactId;
    if (props.initialContact) {
      selectedContact.value = props.initialContact;
      contactQuery.value = props.initialContact.name || '';
    }
  }

  if (!form.value.inbox_id && showInboxSelector.value) {
    form.value.inbox_id = fallbackInboxId;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleDocumentClick);
  document.removeEventListener('keydown', handleEscapeKey);
});

const priorityOptions = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

const statusOptions = [
  { value: 'active', label: 'Ativa' },
  { value: 'won', label: 'Ganha' },
  { value: 'lost', label: 'Perdida' },
  { value: 'archived', label: 'Arquivada' },
];

const priorityLabels = { low: 'Baixa', medium: 'Média', high: 'Alta', urgent: 'Urgente' };
const statusLabels = { active: 'Ativo', won: 'Ganho', lost: 'Perdido', archived: 'Arquivado' };

function onContactInput() {
  selectedContact.value = null;
  form.value.contact_id = '';
  showContactDropdown.value = contactResults.value.length > 0;
}

async function searchContacts() {
  if (allContacts.value.length > 0) return;

  isLoadingContacts.value = true;
  try {
    let page = 1;
    let hasMore = true;
    const contacts = [];

    while (hasMore) {
      const { data } = await ContactAPI.get(page, 'name');
      const payload = data.payload || [];
      contacts.push(...payload);
      hasMore = payload.length > 0;
      page += 1;
    }

    allContacts.value = contacts;
  } catch {
    allContacts.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
}

async function onContactFocus() {
  await searchContacts();
  showContactDropdown.value = contactResults.value.length > 0;
}

function handleDocumentClick(event) {
  const target = event.target;

  if (
    showContactDropdown.value &&
    contactDropdownContainerRef.value &&
    !contactDropdownContainerRef.value.contains(target)
  ) {
    showContactDropdown.value = false;
  }

  showProductDropdownByRow.value = showProductDropdownByRow.value.map((isOpen, index) => {
    if (!isOpen) return false;
    const rowRef = productComboboxRefs.value[index];
    if (!rowRef) return false;
    return rowRef.contains(target);
  });
}

function handleEscapeKey(event) {
  if (event.key !== 'Escape') return;

  showContactDropdown.value = false;
  showProductDropdownByRow.value = showProductDropdownByRow.value.map(() => false);
  emit('close');
}

function selectContact(contact) {
  selectedContact.value = contact;
  form.value.contact_id = contact.id;
  contactQuery.value = contact.name || contact.email || `#${contact.id}`;
  showContactDropdown.value = false;
}

function clearContact() {
  selectedContact.value = null;
  form.value.contact_id = '';
  contactQuery.value = '';
  showContactDropdown.value = allContacts.value.length > 0;
  submitError.value = '';
}

watch(
  () => form.value.inbox_id,
  inboxId => {
    if (!showInboxSelector.value) return;

    if (!inboxId) {
      form.value.crm_stage_id = '';
      return;
    }

    const currentStageStillValid = filteredStages.value.some(
      stage => stage.id === Number(form.value.crm_stage_id)
    );

    if (!currentStageStillValid) {
      form.value.crm_stage_id = '';
    }
  }
);

function onAttachmentChange(event) {
  const files = Array.from(event.target?.files || []);
  if (files.length > 0) {
    newAttachments.value = [...newAttachments.value, ...files];
  }
  event.target.value = '';
}

function removeExistingAttachment(attachmentId) {
  existingAttachments.value = existingAttachments.value.filter(
    attachment => attachment.id !== attachmentId
  );
}

function removeNewAttachment(index) {
  newAttachments.value.splice(index, 1);
}

async function handleSubmit() {
  submitError.value = '';
  if (!form.value.title || !form.value.crm_stage_id) return;
  if (!form.value.contact_id) {
    submitError.value = 'Selecione um contato para criar o card.';
    return;
  }
  isSubmitting.value = true;

  const payload = { ...form.value };
  payload.deal_products = dealProducts.value
    .filter(dp => dp.crm_product_id)
    .map(dp => ({
      crm_product_id: Number(dp.crm_product_id),
      quantity: dp.quantity,
      unit_price: dp.unit_price,
    }));

  payload.attachments = newAttachments.value;
  payload.retained_attachment_ids = existingAttachments.value.map(attachment => attachment.id);

  try {
    if (isEditing.value) {
      await store.dispatch('crmDeals/update', {
        id: props.deal.id,
        ...payload,
      });
    } else {
      await store.dispatch('crmDeals/create', payload);
    }
    emit('saved');
    emit('close');
  } catch (error) {
    submitError.value = error?.response?.data?.message || 'Nao foi possivel salvar o card.';
  } finally {
    isSubmitting.value = false;
  }
}

async function handleDelete() {
  if (!isEditing.value) return;
  if (!confirm('Tem certeza que deseja excluir este card?')) return;
  try {
    await store.dispatch('crmDeals/delete', props.deal.id);
    emit('saved');
  } catch (error) {
    // handle error
  }
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-5 py-3.5 border-b border-n-weak"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ isEditing ? 'Editar Card' : 'Novo Card' }}
        </h3>
        <NextButton
          slate
          ghost
          xs
          icon="i-lucide-x"
          @click="emit('close')"
        />
      </div>

      <!-- Form -->
      <form class="p-5 space-y-4 max-h-[70vh] overflow-y-auto" @submit.prevent="handleSubmit">
        <!-- Title -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Título *</label
          >
          <input
            v-model="form.title"
            type="text"
            required
            placeholder="Nome do card"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>

        <!-- Inbox -->
        <div v-if="showInboxSelector">
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Inbox *</label
          >
          <select
            v-model="form.inbox_id"
            required
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          >
            <option value="" disabled>Selecione uma inbox</option>
            <option
              v-for="inbox in inboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Stage -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Estágio *</label
          >
          <select
            v-model="form.crm_stage_id"
            required
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          >
            <option value="" disabled>Selecione um estágio</option>
            <option
              v-for="stage in filteredStages"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>

        <!-- Contact Search -->
        <div ref="contactDropdownContainerRef" class="relative">
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Contato *</label
          >
          <div class="relative">
            <input
              v-model="contactQuery"
              type="text"
              placeholder="Buscar contato por nome, email ou telefone..."
              autocomplete="off"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 pr-8 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              @input="onContactInput"
              @focus="onContactFocus"
            />
            <button
              v-if="selectedContact"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-12"
              @click="clearContact"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
            <span
              v-if="isLoadingContacts"
              class="i-lucide-loader-2 animate-spin absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-9"
            />
          </div>
          <!-- Selected contact pill -->
          <div
            v-if="selectedContact"
            class="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-blue-3 text-n-blue-11 text-xs"
          >
            <span class="i-lucide-user w-3 h-3" />
            <span>{{ selectedContact.name || selectedContact.email }}</span>
            <span class="text-n-blue-8">#{{ selectedContact.id }}</span>
          </div>
          <!-- Dropdown -->
          <div
            v-if="showContactDropdown"
            class="absolute z-50 w-full mt-1 bg-n-solid-2 border border-n-weak rounded-lg shadow-lg max-h-48 overflow-y-auto"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-n-slate-3 transition-colors"
              @click="selectContact(contact)"
            >
              <span
                class="w-7 h-7 rounded-full bg-n-blue-9 text-white text-[10px] flex items-center justify-center font-medium flex-shrink-0"
              >
                {{ (contact.name || '?').charAt(0).toUpperCase() }}
              </span>
              <div class="flex-1 min-w-0">
                <p class="text-xs font-medium text-n-slate-12 truncate">
                  {{ contact.name || 'Sem nome' }}
                </p>
                <p class="text-[10px] text-n-slate-10 truncate">
                  {{ contact.email || contact.phone_number || `ID: ${contact.id}` }}
                </p>
              </div>
            </button>
            <p
              v-if="!isLoadingContacts && contactResults.length === 0"
              class="px-3 py-2 text-xs text-n-slate-10"
            >
              Nenhum contato encontrado.
            </p>
          </div>
        </div>

        <p v-if="submitError" class="text-xs text-n-ruby-11">
          {{ submitError }}
        </p>

        <!-- Products / Services -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-xs font-medium text-n-slate-11">Produtos / Serviços</label>
            <button
              type="button"
              class="text-[10px] text-n-blue-11 hover:text-n-blue-12 font-medium flex items-center gap-0.5"
              @click="addProductRow"
            >
              <span class="i-lucide-plus w-3 h-3" />
              Adicionar
            </button>
          </div>
          <div v-if="dealProducts.length > 0" class="space-y-2">
            <div
              v-for="(dp, idx) in dealProducts"
              :key="idx"
              class="grid grid-cols-[1fr_4rem_5rem_1.5rem_1.5rem] items-center gap-2"
            >
              <div
                :ref="el => setProductComboboxRef(el, idx)"
                class="relative"
              >
                <input
                  v-model="productQueryByRow[idx]"
                  type="text"
                  placeholder="Pesquisar produto/serviço"
                  class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-xs text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
                  @focus="onProductInputFocus(idx)"
                  @input="onProductInput(idx)"
                />
                <div
                  v-if="showProductDropdownByRow[idx]"
                  class="absolute z-50 mt-1 w-full rounded-lg border border-n-weak bg-n-solid-2 shadow-lg max-h-44 overflow-y-auto"
                >
                  <button
                    v-for="product in filteredProductsByRow[idx]"
                    :key="product.id"
                    type="button"
                    class="w-full px-2 py-1.5 text-left text-xs text-n-slate-12 hover:bg-n-slate-3"
                    @click="selectProduct(idx, product)"
                  >
                    {{ product.name }} — {{ formatCurrency(product.unit_price) }}
                  </button>
                  <p
                    v-if="filteredProductsByRow[idx].length === 0"
                    class="px-2 py-1.5 text-xs text-n-slate-10"
                  >
                    Nenhum produto encontrado.
                  </p>
                </div>
              </div>
              <input
                v-model.number="dp.quantity"
                type="number"
                min="1"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-xs text-n-slate-12 text-center focus:outline-none focus:ring-2 focus:ring-n-blue-7"
                placeholder="Qtd"
                @input="recalcTotalFromProducts"
              />
              <span class="text-xs font-medium text-n-slate-11 text-right">
                {{ formatCurrency(dp.quantity * dp.unit_price) }}
              </span>
              <button
                type="button"
                class="text-n-slate-9 hover:text-n-blue-11"
                :disabled="!dp.crm_product_id"
                title="Editar produto selecionado"
                @click="openSelectedProductEditor(idx)"
              >
                <span class="i-lucide-pencil w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                class="text-n-slate-9 hover:text-n-ruby-11"
                @click="removeProductRow(idx)"
              >
                <span class="i-lucide-x w-3.5 h-3.5" />
              </button>
            </div>
            <div class="flex justify-end pt-1 border-t border-n-weak">
              <span class="text-xs font-semibold text-n-slate-12">
                Total: {{ formatCurrency(form.value) }}
              </span>
            </div>
          </div>
          <p v-else class="text-[10px] text-n-slate-9">
            Nenhum produto adicionado. O valor pode ser informado manualmente abaixo.
          </p>
        </div>

        <!-- Attachments -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-xs font-medium text-n-slate-11">Anexos</label>
            <span class="text-[10px] text-n-slate-10">Opcional</span>
          </div>

          <div v-if="existingAttachments.length > 0 || newAttachments.length > 0" class="mb-2 space-y-2">
            <div
              v-for="attachment in existingAttachments"
              :key="`existing-${attachment.id}`"
              class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
            >
              <div class="min-w-0">
                <p class="truncate text-xs text-n-slate-12">{{ attachment.filename }}</p>
                <p class="text-[10px] text-n-slate-10">Arquivo salvo</p>
              </div>
              <button
                type="button"
                class="text-n-slate-9 hover:text-n-ruby-11"
                @click="removeExistingAttachment(attachment.id)"
              >
                <span class="i-lucide-x w-3.5 h-3.5" />
              </button>
            </div>

            <div
              v-for="(attachment, index) in newAttachments"
              :key="`new-${attachment.name}-${index}`"
              class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
            >
              <div class="min-w-0">
                <p class="truncate text-xs text-n-slate-12">{{ attachment.name }}</p>
                <p class="text-[10px] text-n-slate-10">Novo arquivo</p>
              </div>
              <button
                type="button"
                class="text-n-slate-9 hover:text-n-ruby-11"
                @click="removeNewAttachment(index)"
              >
                <span class="i-lucide-x w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <label class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 cursor-pointer hover:bg-n-slate-2 transition-colors">
            <span class="text-xs text-n-slate-11 truncate">Adicionar um ou mais arquivos</span>
            <span class="text-[10px] text-n-blue-11 font-medium">Selecionar</span>
            <input
              type="file"
              multiple
              class="hidden"
              @change="onAttachmentChange"
            />
          </label>
        </div>

        <!-- Value -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Valor (R$)</label
          >
          <input
            v-model.number="form.value"
            type="number"
            step="0.01"
            min="0"
            placeholder="0,00"
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>

        <div class="flex gap-3">
          <!-- Priority -->
          <div class="flex-1">
            <label class="block text-xs font-medium text-n-slate-11 mb-1"
              >Prioridade</label
            >
            <select
              v-model="form.priority"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
            >
              <option
                v-for="opt in priorityOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>

          <!-- Status -->
          <div v-if="isEditing" class="flex-1">
            <label class="block text-xs font-medium text-n-slate-11 mb-1"
              >Status</label
            >
            <select
              v-model="form.status"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
            >
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Description -->
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1"
            >Descrição</label
          >
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Descreva este card..."
            class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 resize-none focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-between pt-2">
          <div>
            <NextButton
              v-if="isEditing"
              label="Excluir"
              icon="i-lucide-trash-2"
              ruby
              faded
              sm
              type="button"
              @click="handleDelete"
            />
          </div>
          <div class="flex items-center gap-2">
            <NextButton
              label="Cancelar"
              slate
              faded
              sm
              type="button"
              @click="emit('close')"
            />
            <NextButton
              :label="isEditing ? 'Salvar' : 'Criar'"
              icon="i-lucide-check"
              blue
              sm
              type="submit"
              :is-loading="isSubmitting"
              :disabled="isSubmitDisabled"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
